/**
 * Owner: Masa Bayu
 * File: FilterSidebar.tsx
 * Date:Time: 2026-10-22 23:58
 * Description: Sidebar penuras — carian + Kategori/Harga mengikut kiraan katalog hub sebenar.
 *   §405 — senarai kategori ialah taksonomi QX penuh (35) dengan nama dari qxCategoryName;
 *   subkategori muncul sebagai sekatan kedua bila satu kategori dipilih.
 *   §466 S1 — sekatan Best Book/Penilaian dibuang: tiada penarafan/lencana rekaan.
 */

import type { ReactElement } from 'react';
import {
  MARKETPLACE_CATEGORY_ORDER,
  MARKETPLACE_PRICE_BAND_ORDER,
  type MarketplaceFacetCounts,
  type MarketplaceFilters,
} from './marketplace-projection';
import { type MarketplaceLabels } from './marketplace-locale';
import { qxCategoryName, qxSubcategoriesOf } from '@qx/qxread-core';

export interface FilterSidebarProps {
  readonly t: MarketplaceLabels;
  readonly filters: MarketplaceFilters;
  readonly counts: MarketplaceFacetCounts;
  readonly onChange: (filters: MarketplaceFilters) => void;
}

export function FilterSidebar({ t, filters, counts, onChange }: FilterSidebarProps): ReactElement {
  const selected = filters.category === 'all' ? null : filters.category;
  const subcategories = selected === null ? [] : qxSubcategoriesOf(selected);
  return (
    <aside className="hmk__sidebar" aria-label={t.sidebar.ariaLabel}>
      <div className="hmk__sidebar-search">
        <label className="visually-hidden" htmlFor="hmk-search">
          {t.sidebar.searchLabel}
        </label>
        <input
          id="hmk-search"
          type="search"
          role="searchbox"
          placeholder={t.sidebar.searchPlaceholder}
          value={filters.query}
          onChange={(event) => {
            onChange({ ...filters, query: event.target.value });
          }}
        />
      </div>

      <fieldset className="hmk__filter-group">
        <legend className="hmk__filter-title">{t.sidebar.categoryLegend}</legend>
        <label className="hmk__filter-row" data-checked={filters.category === 'all'}>
          <input
            type="radio"
            name="hmk-category"
            checked={filters.category === 'all'}
            onChange={() => {
              onChange({ ...filters, category: 'all', subcategory: 'all' });
            }}
          />
          <span className="hmk__filter-label">{t.sidebar.allCategories}</span>
          <span className="hmk__filter-count">{counts.categoryTotal}</span>
        </label>
        {MARKETPLACE_CATEGORY_ORDER.filter((categoryId) => counts.categories[categoryId] > 0).map(
          (categoryId) => (
            <label
              key={categoryId}
              className="hmk__filter-row"
              data-checked={filters.category === categoryId}
            >
              <input
                type="radio"
                name="hmk-category"
                checked={filters.category === categoryId}
                onChange={() => {
                  onChange({ ...filters, category: categoryId, subcategory: 'all' });
                }}
              />
              <span className="hmk__filter-label">{qxCategoryName(categoryId, t.language)}</span>
              <span className="hmk__filter-count">{counts.categories[categoryId]}</span>
            </label>
          ),
        )}
      </fieldset>

      {selected !== null && subcategories.length > 0 && (
        <fieldset className="hmk__filter-group">
          <legend className="hmk__filter-title">
            {qxCategoryName(selected, t.language)} — {t.sidebar.subcategoryLegend}
          </legend>
          <label className="hmk__filter-row" data-checked={filters.subcategory === 'all'}>
            <input
              type="radio"
              name="hmk-subcategory"
              checked={filters.subcategory === 'all'}
              onChange={() => {
                onChange({ ...filters, subcategory: 'all' });
              }}
            />
            <span className="hmk__filter-label">{t.sidebar.allSubcategories}</span>
          </label>
          {subcategories.map((sub) => (
            <label
              key={sub.id}
              className="hmk__filter-row"
              data-checked={filters.subcategory === sub.id}
            >
              <input
                type="radio"
                name="hmk-subcategory"
                checked={filters.subcategory === sub.id}
                onChange={() => {
                  onChange({ ...filters, subcategory: sub.id });
                }}
              />
              <span className="hmk__filter-label">{qxCategoryName(sub.id, t.language)}</span>
              <span className="hmk__filter-count">{counts.subcategories[sub.id] ?? 0}</span>
            </label>
          ))}
        </fieldset>
      )}

      <fieldset className="hmk__filter-group">
        <legend className="hmk__filter-title">{t.sidebar.priceLegend}</legend>
        <label className="hmk__filter-row" data-checked={filters.price === 'all'}>
          <input
            type="radio"
            name="hmk-price"
            checked={filters.price === 'all'}
            onChange={() => {
              onChange({ ...filters, price: 'all' });
            }}
          />
          <span className="hmk__filter-label">{t.sidebar.allPrices}</span>
          <span className="hmk__filter-count">{counts.priceTotal}</span>
        </label>
        {MARKETPLACE_PRICE_BAND_ORDER.map((band) => (
          <label key={band} className="hmk__filter-row" data-checked={filters.price === band}>
            <input
              type="radio"
              name="hmk-price"
              checked={filters.price === band}
              onChange={() => {
                onChange({ ...filters, price: band });
              }}
            />
            <span className="hmk__filter-label">{t.priceBands[band]}</span>
            <span className="hmk__filter-count">{counts.prices[band]}</span>
          </label>
        ))}
      </fieldset>
    </aside>
  );
}
